import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BackendProvider,
  ChargePoint,
  ChargePointDetail,
  ChargePointOnlineStatus,
  ChargingEvent,
  ChargingStationBackendData,
  ChargingStationBackendDataRequest,
  CreatePricePolicyRequest,
  Driver,
  DriverInvitePayload,
  DriverKey,
  DriverKeyFull,
  DriverOrganization,
  EdiKey,
  Export,
  FlatratePrice,
  LatestPriceResponse,
  Mileage,
  OrganizationPricePolicy,
  Reimbursement,
} from '@core/driver/driver.models';
import { CRAPIError } from '@shared/models/error.models';
import { catchError, from, map, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class DriverAPIService {
  private readonly http = inject(HttpClient);

  // PC-IAM Endpoints
  private readonly baseCRURI = environment.chargeRepayBaseURL;

  private readonly organizationEndpoint: string = `${this.baseCRURI}/v2/organization`;
  private readonly organizationDriversEndpoint: string = `${this.organizationEndpoint}/drivers`;
  private readonly chargePointsEndpoint: string = `${this.baseCRURI}/v2/charge-points`;
  private readonly csvExampleEndpoint: string = `${this.organizationEndpoint}/driver-invite-example-csv`;
  private readonly ediKeyEndpoint: string = `${this.organizationEndpoint}/keys`;
  private readonly backendProviderEndpoint: string = `${this.baseCRURI}/v2/backend-provider`;
  private readonly chargingEventsEndpoint: string = `${this.organizationEndpoint}/charging-events`;
  private readonly chargingEventsCsvEndpoint: string = `${this.chargingEventsEndpoint}/csv`;
  private readonly exportsEndpoint: string = `${this.organizationEndpoint}/exports`;
  private readonly exportsDownloadEndpoint: string = `${this.exportsEndpoint}/download`;
  private readonly reimbursementsEndpoint: string = `${this.organizationEndpoint}/reimbursements`;
  private readonly carsEndpoint: string = `${this.baseCRURI}/v2/cars`;
  private readonly flatratePricesEndpoint: string = `${this.baseCRURI}/v2/flatrate-prices`;

  public getDrivers() {
    return this.http
      .get<Driver[]>(this.organizationDriversEndpoint, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        catchError((error) => {
          return this.handleError(error);
        }),
      );
  }

  public getChargePoints() {
    return this.http
      .get<ChargePoint[]>(this.chargePointsEndpoint)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public getConnectionState(uuid: string) {
    const url = `${this.chargePointsEndpoint}/${uuid}/online-status`;
    return this.http
      .get<ChargePointOnlineStatus>(url)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  public getDriverOrganization() {
    return this.http
      .get<DriverOrganization>(this.organizationEndpoint)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public getFlatratePrices() {
    return this.http
      .get<FlatratePrice[]>(this.flatratePricesEndpoint)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public createPricePolicy(payload: CreatePricePolicyRequest) {
    const url = `${this.organizationEndpoint}/price-policies`;
    return this.http
      .post<OrganizationPricePolicy>(url, payload)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public deletePricePolicy() {
    const url = `${this.organizationEndpoint}/price-policies`;
    return this.http.delete<void>(url).pipe(catchError((err) => this.handleError(err)));
  }

  public getCSVExample() {
    return this.http
      .get(this.csvExampleEndpoint, { responseType: 'blob', observe: 'response' })
      .pipe(catchError((err) => this.handleError(err)));
  }

  public sendSingleInvite(payload: DriverInvitePayload) {
    return this.http
      .post(this.organizationDriversEndpoint, [payload])
      .pipe(catchError((err) => this.handleError(err)));
  }

  public sendBulkInvite(payloads: DriverInvitePayload[]) {
    return this.http
      .post(this.organizationDriversEndpoint, payloads)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public deleteInvitedDriver(userId: string) {
    const url = `${this.organizationDriversEndpoint}/${userId}`;
    return this.http.delete<void>(url).pipe(catchError((err) => this.handleError(err)));
  }

  public updateOrganization(payload: Partial<DriverOrganization>) {
    return this.http
      .put<DriverOrganization>(this.organizationEndpoint, payload)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public getOrganizationKeys() {
    return this.http.get<{ keys: EdiKey[] } | EdiKey[]>(this.ediKeyEndpoint).pipe(
      // Normalise: backend may return { keys: [...] } or a bare EdiKey[] array
      map((response) => (Array.isArray(response) ? response : (response?.keys ?? []))),
      catchError((err) => this.handleError(err)),
    );
  }

  public addEdi(ediKey: EdiKey) {
    return this.http
      .post<{ key: string }>(this.ediKeyEndpoint, { keys: [ediKey] })
      .pipe(catchError((err) => this.handleError(err)));
  }

  public removeEdi(key: string, force: boolean = false) {
    return this.http
      .delete(`${this.ediKeyEndpoint}?key=${key}&forced=${force}`)
      .pipe(catchError((err) => this.handleError(err)));
  }

  // Returns null unless the provider call fails with a 5xx.
  public getBackendProvider() {
    return this.http.get<BackendProvider>(this.backendProviderEndpoint).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status >= 500) {
          return this.handleError(err);
        }
        return throwError(() => null);
      }),
    );
  }

  public createBackendProvider(payload: Partial<BackendProvider>) {
    return this.http
      .post<BackendProvider>(this.backendProviderEndpoint, payload)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public updateBackendProvider(payload: Partial<BackendProvider>) {
    return this.http
      .put<BackendProvider>(this.backendProviderEndpoint, payload)
      .pipe(catchError((err) => this.handleError(err)));
  }

  // Pass yearMonth as YYYY-MM; omit it to return every month.
  public getChargingEvents(yearMonth?: string) {
    let url = this.chargingEventsEndpoint;
    if (yearMonth) {
      url += `?year_month=${yearMonth}`;
    }
    return this.http.get<ChargingEvent[]>(url).pipe(catchError((err) => this.handleError(err)));
  }

  public getChargingEventsByRange(startDate: string, endDate: string) {
    const url = `${this.chargingEventsEndpoint}?start_date=${startDate}&end_date=${endDate}`;
    return this.http.get<ChargingEvent[]>(url).pipe(catchError((err) => this.handleError(err)));
  }

  // year_month takes priority over start_date and end_date.
  public getChargingEventsCsv(params: {
    yearMonth?: string;
    startDate?: string;
    endDate?: string;
    uuid?: string;
  }) {
    const queryParams: string[] = [];
    if (params.yearMonth) {
      queryParams.push(`year_month=${encodeURIComponent(params.yearMonth)}`);
    } else if (params.startDate && params.endDate) {
      queryParams.push(`start_date=${encodeURIComponent(params.startDate)}`);
      queryParams.push(`end_date=${encodeURIComponent(params.endDate)}`);
    }
    if (params.uuid) {
      queryParams.push(`uuid=${encodeURIComponent(params.uuid)}`);
    }

    const url = queryParams.length
      ? `${this.chargingEventsCsvEndpoint}?${queryParams.join('&')}`
      : this.chargingEventsCsvEndpoint;
    return this.http
      .get(url, { responseType: 'blob', observe: 'response' })
      .pipe(catchError((err) => this.handleError(err)));
  }

  public getChargingEventsByChargePoint(cp: string, yearMonth?: string) {
    let url = `${this.chargePointsEndpoint}/${cp}/charging-events`;
    if (yearMonth) {
      url += `?year_month=${yearMonth}`;
    }
    return this.http.get<ChargingEvent[]>(url).pipe(catchError((err) => this.handleError(err)));
  }

  public getLatestPrice(uuid: string) {
    const url = `${this.chargePointsEndpoint}/${uuid}/prices/latest`;
    return this.http.get<LatestPriceResponse>(url).pipe(catchError((err) => this.handleError(err)));
  }

  public getMileagesOfCar(carId: string) {
    const url = `${this.carsEndpoint}/${carId}/mileage`;
    return this.http.get<Mileage[]>(url).pipe(catchError((err) => this.handleError(err)));
  }

  public getDriverKeys(userId: string) {
    const url = `${this.organizationDriversEndpoint}/${userId}/keys`;
    return this.http.get<DriverKeyFull[]>(url).pipe(catchError((err) => this.handleError(err)));
  }

  public createDriverKey(userId: string, payload: DriverKey) {
    const url = `${this.organizationDriversEndpoint}/${userId}/keys`;
    return this.http
      .post<DriverKeyFull>(url, payload)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public updateDriverKey(userId: string, payload: DriverKeyFull) {
    const url = `${this.organizationDriversEndpoint}/${userId}/keys/${payload.information_id}`;
    return this.http
      .put<DriverKeyFull>(url, payload)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public getExports() {
    return this.http.get<Export[]>(this.exportsEndpoint).pipe(
      catchError((err) => {
        return this.handleError(err);
      }),
    );
  }

  public getReimbursements() {
    return this.http
      .get<Reimbursement[]>(this.reimbursementsEndpoint)
      .pipe(catchError((err) => this.handleError(err)));
  }

  public getExport(documentId: string) {
    const url = `${this.exportsDownloadEndpoint}/${documentId}`;
    return this.http.get(url, { responseType: 'blob', observe: 'response' }).pipe(
      catchError((err) => {
        return this.handleError(err);
      }),
    );
  }

  // Preview export ZIP: ispreview=true, force=false.
  public postOrganizationExport(yearMonth: string, locale: string, isPreview: boolean) {
    const url = `${this.exportsEndpoint}?ispreview=${isPreview}&force=false&locale=${encodeURIComponent(locale)}`;
    return this.http
      .post(url, { year_month: yearMonth }, { responseType: 'blob', observe: 'response' })
      .pipe(catchError((err: HttpErrorResponse) => this.handleExportError(err)));
  }

  private handleExportError(error: HttpErrorResponse) {
    if (error.error instanceof Blob) {
      return from(error.error.text()).pipe(
        switchMap((text) => {
          let errorCode = 'UNKNOWN_ERROR';
          try {
            const parsed = JSON.parse(text);
            errorCode = parsed?.error?.id ?? 'UNKNOWN_ERROR';
          } catch {}
          return throwError(
            () => ({ statusCode: error.status, error_code: errorCode }) as CRAPIError,
          );
        }),
      );
    }
    const errorCode: string = error?.error?.error?.id ?? 'UNKNOWN_ERROR';
    return throwError(
      () => ({ statusCode: error?.status ?? 0, error_code: errorCode }) as CRAPIError,
    );
  }

  public getEnergyContract(uuid: string, priceId: number) {
    const url = `${this.chargePointsEndpoint}/${uuid}/prices/${priceId}/contract`;
    return this.http
      .get(url, { responseType: 'blob', observe: 'response' })
      .pipe(catchError((err) => this.handleError(err)));
  }

  public getChargingStationBackendData(uuid: string) {
    const url = `${this.backendProviderEndpoint}/charging-station/${uuid}`;
    return this.http
      .get<ChargingStationBackendData>(url)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  public getChargePointDetail(uuid: string) {
    const url = `${this.chargePointsEndpoint}/${uuid}?include_metadata=true`;
    return this.http
      .get<ChargePointDetail>(url)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  public createChargingStationBackendData(
    uuid: string,
    payload: ChargingStationBackendDataRequest,
  ) {
    const url = `${this.backendProviderEndpoint}/charging-station/${uuid}`;
    return this.http
      .post<ChargingStationBackendData>(url, payload)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  public updateChargingStationBackendData(
    uuid: string,
    payload: ChargingStationBackendDataRequest,
  ) {
    const url = `${this.backendProviderEndpoint}/charging-station/${uuid}`;
    return this.http
      .put<ChargingStationBackendData>(url, payload)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  public testBackendProviderConnection(uuid: string) {
    const url = `${this.backendProviderEndpoint}/charging-station/${uuid}/backend-provider-connection`;
    return this.http
      .get<void>(url)
      .pipe(catchError((err: HttpErrorResponse) => this.handleError(err)));
  }

  private handleError(error: HttpErrorResponse) {
    console.error(error);

    const nestedError = error?.error?.error ?? error?.error;
    const errorMessage: CRAPIError = {
      statusCode: error?.status ?? 0,
      error_code: nestedError?.id ?? 'UNKNOWN_ERROR',
      message: nestedError?.message,
    };

    return throwError(() => errorMessage);
  }
}
